import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft, FiCalendar, FiPlus, FiTrash2, FiZap, FiClock, FiMapPin
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { reuniaoCriancasService, congregacaoService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import './ReuniaoCriancas.css';

const SEMANAS_LABEL = {
  1: '1a semana', 2: '2a semana', 3: '3a semana', 4: '4a semana', 5: '5a semana'
};

const ReuniaoCriancasAgenda = () => {
  const navigate = useNavigate();
  const { canWriteModule } = useAuth();
  const podeEditar = canWriteModule('reuniao_criancas');

  const [congregacoes, setCongregacoes] = useState([]);
  const [meses, setMeses] = useState(12);
  const [apartirDe, setApartirDe] = useState('');
  const [regras, setRegras] = useState([
    { semanas: [1, 2, 3, 4, 5], congregacaoNome: 'Central', horario: '09:00' },
  ]);
  const [gerando, setGerando] = useState(false);

  useEffect(() => {
    congregacaoService.getAtivas().then((r) => {
      const nomes = (r?.data || []).map((c) => c.nome || c.nomeCompleto).filter(Boolean);
      setCongregacoes(nomes);
    }).catch(() => {});
  }, []);

  const toggleSemana = (idx, semana) => {
    setRegras((prev) => {
      const novo = [...prev];
      const r = { ...novo[idx] };
      const semanas = [...r.semanas];
      const i = semanas.indexOf(semana);
      if (i >= 0) semanas.splice(i, 1);
      else semanas.push(semana);
      semanas.sort();
      r.semanas = semanas;
      novo[idx] = r;
      return novo;
    });
  };

  const atualizarRegra = (idx, campo, valor) => {
    setRegras((prev) => {
      const novo = [...prev];
      novo[idx] = { ...novo[idx], [campo]: valor };
      return novo;
    });
  };

  const removerRegra = (idx) => {
    setRegras((prev) => prev.filter((_, i) => i !== idx));
  };

  const adicionarRegra = () => {
    setRegras((prev) => [
      ...prev,
      { semanas: [], congregacaoNome: '', horario: '09:00' },
    ]);
  };

  const gerar = async () => {
    const valido = regras.every((r) => r.semanas.length > 0 && r.congregacaoNome && r.horario);
    if (!valido) {
      toast.warn('Preencha todas as regras (semanas, congregacao e horario)');
      return;
    }
    if (!window.confirm(`Gerar agenda para os proximos ${meses} meses?`)) return;
    try {
      setGerando(true);
      const resp = await reuniaoCriancasService.gerarRecorrentes({
        meses,
        regras,
        apartirDe: apartirDe || undefined,
      });
      toast.success(
        `Agenda gerada: ${resp.criadas} reuniao(oes) criada(s)` +
        (resp.ignoradas > 0 ? `, ${resp.ignoradas} ja existiam` : '')
      );
      setTimeout(() => navigate('/reuniao-criancas'), 1200);
    } catch {
      toast.error('Erro ao gerar agenda');
    } finally {
      setGerando(false);
    }
  };

  return (
    <div className="rc-page">
      <button className="rc-back" onClick={() => navigate('/reuniao-criancas')}>
        <FiArrowLeft /> Voltar
      </button>

      <header className="rc-page-header">
        <div>
          <h1><FiCalendar className="rc-icon-title" /> Agenda recorrente</h1>
          <p>Configure quais congregacoes recebem a reuniao em cada sabado do mes</p>
        </div>
      </header>

      <div className="rc-aviso-info">
        <strong>Como funciona:</strong> a reuniao de criancas acontece todo sabado. Defina qual
        congregacao recebe em cada semana do mes (1a, 2a, 3a, 4a, 5a) e o horario. O sistema gera
        as proximas reunioes automaticamente.
      </div>

      <section className="rc-section">
        <h2>Regras da agenda</h2>

        <div className="rc-regras-lista">
          {regras.map((r, idx) => (
            <div key={idx} className="rc-regra">
              <div className="rc-regra-header">
                <strong>Regra {idx + 1}</strong>
                {regras.length > 1 && (
                  <button
                    className="rc-btn-icone rc-btn-danger"
                    onClick={() => removerRegra(idx)}
                    disabled={!podeEditar}
                    title="Remover"
                  >
                    <FiTrash2 />
                  </button>
                )}
              </div>

              <div className="rc-regra-corpo">
                <div className="rc-regra-campo">
                  <label>Semanas do mes</label>
                  <div className="rc-semanas">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <button
                        key={s}
                        type="button"
                        className={`rc-chip ${r.semanas.includes(s) ? 'rc-chip-on' : ''}`}
                        onClick={() => toggleSemana(idx, s)}
                        disabled={!podeEditar}
                      >
                        {SEMANAS_LABEL[s]}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="rc-regra-row">
                  <label className="rc-regra-campo">
                    <span><FiMapPin /> Congregacao</span>
                    <select
                      value={r.congregacaoNome}
                      onChange={(e) => atualizarRegra(idx, 'congregacaoNome', e.target.value)}
                      disabled={!podeEditar}
                    >
                      <option value="">Selecione...</option>
                      {congregacoes.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </label>

                  <label className="rc-regra-campo">
                    <span><FiClock /> Horario</span>
                    <input
                      type="time"
                      value={r.horario}
                      onChange={(e) => atualizarRegra(idx, 'horario', e.target.value)}
                      disabled={!podeEditar}
                    />
                  </label>
                </div>
              </div>
            </div>
          ))}

          {podeEditar && (
            <button className="rc-btn rc-btn-ghost rc-btn-add-regra" onClick={adicionarRegra}>
              <FiPlus /> Adicionar regra
            </button>
          )}
        </div>
      </section>

      <section className="rc-section">
        <h2>Periodo</h2>
        <p className="rc-section-sub">
          Para gerar reunioes retroativas, defina a data inicial no passado.
          Reunioes que ja existem serao ignoradas automaticamente.
        </p>
        <div className="rc-form-grid">
          <label>
            <span><FiCalendar /> A partir de (opcional)</span>
            <input
              type="date"
              value={apartirDe}
              onChange={(e) => setApartirDe(e.target.value)}
              placeholder="Padrao: hoje"
            />
          </label>
          <label>
            <span>Duracao</span>
            <select value={meses} onChange={(e) => setMeses(parseInt(e.target.value, 10))}>
              <option value={1}>1 mes</option>
              <option value={2}>2 meses</option>
              <option value={3}>3 meses</option>
              <option value={6}>6 meses</option>
              <option value={12}>12 meses</option>
              <option value={24}>24 meses</option>
            </select>
          </label>
        </div>
      </section>

      {podeEditar && (
        <div className="rc-action-bar">
          <button
            className="rc-btn rc-btn-primary"
            onClick={gerar}
            disabled={gerando}
          >
            <FiZap /> {gerando ? 'Gerando...' : 'Gerar agenda'}
          </button>
        </div>
      )}
    </div>
  );
};

export default ReuniaoCriancasAgenda;
